import React, { Component, ErrorInfo, ReactNode } from 'react';
import { parseErrorMessage } from '../utils/errorHandling';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
}

class StreakErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `streak-error-${Date.now()}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('StreakErrorBoundary caught an error:', error, errorInfo);
    
    // Parse the error for user-friendly messaging
    const parsedError = parseErrorMessage(error);
    console.error('Parsed error info:', parsedError);
    
    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorId: ''
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI for streak components
      return (
        <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex-shrink-0">
            <div className="text-2xl opacity-50">🔥</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-red-800 dark:text-red-200">
              Problém se sérií
            </div>
            <div className="text-xs text-red-600 dark:text-red-300 mt-1">
              Něco se pokazilo při zobrazení série. Zkuste obnovit stránku.
            </div>
            <button
              onClick={this.handleRetry}
              className="mt-2 text-xs bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700 text-red-800 dark:text-red-200 px-2 py-1 rounded transition-colors"
            >
              Zkusit znovu
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default StreakErrorBoundary;